import { useEffect, useState } from 'react';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchDroneFlightPlaybackUrl, type DroneFlight } from '../../api/drone-flights.api';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';

/**
 * Tarjeta de "último vuelo de dron" — vídeo confirmado más reciente de la
 * lista de `flights` que se le pase. Extraído de `InvestorModuleDetailPage`
 * (F6) para reusarlo también en `InvestorOverviewPage` (Fase B, "Inicio")
 * sin duplicar la lógica de fetch de URL firmada + manejo de error del
 * `<video>` (URL expirada, archivo corrupto, red).
 */

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  videoCard: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  videoThumbnail: {
    width: '100%',
    maxWidth: '400px',
    height: '225px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

interface Props {
  flights: DroneFlight[];
  title: string;
  loadingLabel: string;
  errorLabel: string;
}

export function DroneVideoCard({ flights, title, loadingLabel, errorLabel }: Props) {
  const s = useStyles();
  const { formatDate } = useLocaleSettings();

  const latestVideo = flights
    .filter(f => f.flightType === 'VIDEO' && f.status === 'CONFIRMED' && f.videoS3Key)
    .sort((a, b) => new Date(b.flightDate).getTime() - new Date(a.flightDate).getTime())[0];

  const { data: playback, isError: playbackFetchError } = useQuery({
    queryKey: ['drone-flight-playback', latestVideo?.flightId],
    queryFn: async () => fetchDroneFlightPlaybackUrl(latestVideo!.flightId),
    enabled: !!latestVideo?.videoS3Key,
  });

  // El fetch de la URL puede salir bien y el <video> igual fallar al cargar
  // (URL firmada expirada, archivo corrupto, red) — sin esto se queda el
  // ícono nativo de "video roto" en vez del mensaje amigable.
  const [videoPlaybackFailed, setVideoPlaybackFailed] = useState(false);
  useEffect(() => setVideoPlaybackFailed(false), [playback?.url]);
  const playbackError = playbackFetchError || videoPlaybackFailed;

  if (!latestVideo) return null;

  return (
    <div className={s.section}>
      <span className={s.sectionTitle}>{title}</span>
      <div className={s.videoCard}>
        {playback?.url && !videoPlaybackFailed ? (
          <video
            controls
            className={s.videoThumbnail}
            src={playback.url}
            onError={() => setVideoPlaybackFailed(true)}
          />
        ) : playbackError ? (
          <div className={s.videoThumbnail}>
            <Text>{errorLabel}</Text>
          </div>
        ) : (
          <div className={s.videoThumbnail}>
            <Text>{loadingLabel}</Text>
          </div>
        )}
        <Text size={200}>{formatDate(latestVideo.flightDate)}</Text>
      </div>
    </div>
  );
}
